import React from 'react';
import IAccreditationApplicationForm from 'interfaces/IAccreditationApplicationForm';
import { MemberProfile } from 'graphql/__generated__/graphql';
import MemberDetailsBlock from './MemberDetailsBlock';
import {
    IApplicationRequirementsShared,
    IAppRequirement,
} from 'interfaces/IApplicationRequirements';
import _ from 'lodash';
import { useDocuments } from 'hooks/useDocuments';
import { Col, Row, Tab } from 'react-bootstrap';
import ListGroup from 'react-bootstrap/ListGroup';
import ClinicalContactReviewEntry from '../../ClinicalContactReviewEntry';
import { IAccreditationApplication } from '../../../../interfaces/IAccreditationApplication';
import ActivityReviewEntry from '../../ActivityReviewEntry';
import { useNotificationContext } from '../../../../contexts/NotificationContext';

interface IApplicationReviewBlockProps {
    formData: IAccreditationApplicationForm;
    memberProfile?: MemberProfile;
    requirements: IApplicationRequirementsShared;
    application: IAccreditationApplication;
}

interface TabContainerProps<T> {
    items: T[];
    renderListItem: (item: T, index: number) => React.ReactNode;
    renderTabContent: (item: T, index: number) => React.ReactNode;
    getKey: (item: T) => string;
}

const TabContainerComponent = <T,>({
    items = [],
    renderListItem,
    renderTabContent,
    getKey,
}: TabContainerProps<T>) => {
    if (!items.length) return null;

    return (
        <Tab.Container id="list-group-tabs-example" defaultActiveKey="#link0">
            <Row>
                <Col sm={3}>
                    <div className="overflow-y-auto" style={{ height: '17em' }}>
                        <ListGroup>
                            {items.map((item, i) => (
                                <ListGroup.Item
                                    action
                                    href={`#link${i}`}
                                    key={`item-${getKey(item)}-${i}`}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    {renderListItem(item, i)}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </div>
                </Col>
                <Col>
                    <Tab.Content>
                        {items.map((item, i) => (
                            <Tab.Pane key={`pane-${getKey(item)}-${i}`} eventKey={`#link${i}`}>
                                {renderTabContent(item, i)}
                            </Tab.Pane>
                        ))}
                    </Tab.Content>
                </Col>
            </Row>
        </Tab.Container>
    );
};

const ApplicationReviewBlock: React.FC<IApplicationReviewBlockProps> = ({
    formData,
    requirements,
    memberProfile,
    application,
}) => {
    const { operations } = useDocuments();
    const { notify } = useNotificationContext();

    const [downloading, setDownloading] = React.useState<string[]>([]);

    const renderList = (items: React.ReactNode[]): React.ReactElement | null => {
        if (!items?.length) return null;
        return (
            <ul className="list-unstyled">
                {items.map((item, index) => (
                    <li key={index}>{item}</li>
                ))}
            </ul>
        );
    };

    const formatValue = (value: any): string => {
        switch (typeof value) {
            case 'undefined':
                return 'unknown';
            case 'boolean':
                return value ? 'completed' : 'incomplete';
            case 'string':
                const lower = value.charAt(0).toLowerCase() + value.slice(1);
                return lower.startsWith('number of ') ? lower.slice(9) : lower;
            default:
                return String(value);
        }
    };

    const getExperienceItems = (): string[] => {
        const mergedExperience = _.merge(
            {},
            _.keyBy(requirements.experience, 'name'),
            _.keyBy(formData.experiences, 'name')
        );

        return _.values(mergedExperience).map((exp) =>
            typeof exp.value === 'boolean'
                ? `${exp.title} ${formatValue(exp.value)}`
                : `${formatValue(exp.value)} ${formatValue(exp.title)}`
        );
    };

    const getDocumentItems = (): React.ReactNode[] => {
        const supplementaryReqs: IAppRequirement[] = Array.from(
            {
                length:
                    formData.documents?.filter((x) => x.name.startsWith('supplementary-')).length +
                    1,
            },
            (_, index) => ({
                name: `supplementary-${index}`,
                title: `Supplementary document ${index + 1}`,
                type: 'document',
            })
        );
        const flattenedDocs: IAppRequirement[] = _.flatMap(
            requirements.documents,
            'requirements'
        ).concat(supplementaryReqs);
        const mergedDocuments = _.values(
            _.merge({}, _.keyBy(flattenedDocs, 'name'), _.keyBy(formData.documents, 'name'))
        );

        return mergedDocuments
            .filter((d) => !!d.value)
            .map((d) => (
                <a
                    onClick={async (e) => {
                        e.preventDefault();
                        setDownloading((prev) => [...prev, d.name]);
                        operations
                            .downloadDocument(d.value as string)
                            .then((success) => {
                                if (!success) {
                                    notify(
                                        'danger',
                                        `Failed to download ${d.title}. Please try again later.`
                                    );
                                } else {
                                    notify('info', `Downloaded ${d.title}.`);
                                }
                            })
                            .finally(() => {
                                setDownloading((prev) => prev.filter((name) => name !== d.name));
                            });
                    }}
                    href="#"
                    title={`Download ${d.title}`}
                    key={d.name}
                >
                    {d.title}{' '}
                    {downloading.includes(d.name) ? (
                        <span className={'badge bg-primary rounded-pill'}>downloading...</span>
                    ) : (
                        ''
                    )}
                </a>
            ));
    };

    const renderSupervisors = () => {
        return renderList(
            formData.supervisors?.map((s) => {
                const startDate = s.startDate
                    ? new Date(s.startDate).toLocaleDateString()
                    : 'unknown date';
                const endDate = s.endDate
                    ? new Date(s.endDate).toLocaleDateString()
                    : s.ongoing
                      ? 'present'
                      : 'unknown date';

                return `${s.name}: ${formatValue(startDate)} - ${endDate}`;
            }) || []
        );
    };

    const renderExperienceSection = () => (
        <dl className="row">
            <dt>Your experience</dt>
            <dd>{renderList(getExperienceItems().map((exp) => exp))}</dd>
        </dl>
    );

    return (
        <>
            {memberProfile && <MemberDetailsBlock memberProfile={memberProfile} />}

            {renderExperienceSection()}

            {application.isRenewal ? (
                <>
                    <dl className="row">
                        <dt className="mb-1">
                            Activity records ({formData.activities?.length || 0})
                        </dt>
                        <dd>
                            <TabContainerComponent
                                items={formData.activities || []}
                                renderListItem={(activity) => activity.eventAndTitle}
                                renderTabContent={(activity) => (
                                    <ActivityReviewEntry activity={activity} />
                                )}
                                getKey={(activity) => activity.eventAndTitle!}
                            />
                        </dd>
                    </dl>
                    <dl className="row">
                        <dt className="col-sm-6">Activity Details</dt>
                        <dd>{formData.activityDetails}</dd>
                    </dl>
                </>
            ) : (
                <>
                    <dl className="row">
                        <dt className="mb-1">
                            Clinical contact records ({formData.clinicalContacts?.length || 0})
                        </dt>
                        <dd>
                            <TabContainerComponent
                                items={formData.clinicalContacts || []}
                                renderListItem={(cc) => cc.initials}
                                renderTabContent={(cc) => <ClinicalContactReviewEntry cc={cc} />}
                                getKey={(cc) => cc.initials!}
                            />
                        </dd>
                    </dl>
                    <dl className="row">
                        <dt className="col-sm-6">Supervisors</dt>
                        <dd>{renderSupervisors()}</dd>
                    </dl>
                </>
            )}

            <dl className="row">
                <dt className="col-sm-6">Documents</dt>
                <dd>{renderList(getDocumentItems())}</dd>
            </dl>
        </>
    );
};

export default ApplicationReviewBlock;
